import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { getFirefoxCssFix } from "../commonfunctions/getFirefoxCssFix";
import DropdownFramer from "../framerMotion/DropdownFramer";
import Image from "../images/Image";
import DropDownModel from "./DropDownModel";
import ErrorMessage from "../ErrorMessages/ErrorMessage";

/**
 * A single user record returned by the users list, carrying the USSD code
 * along with the associated user name and user code.
 */
interface UssdUser {
  user_name: string;
  user_code: string;
  ussd_code: string;
  [key: string]: any;
}

interface UssdCodeDropdownProps {
  onSelect?: (user: UssdUser) => void;
  id?: string;
  className?: string;
  dropdownTopClass?: string;
  testId?: string;
  /** Currently selected USSD code, used to render the trigger label */
  selectedValue?: string | null;
  errorMessage?: string;
  /** Fetches the paginated users list (each user must include a ussd_code) */
  fetchUsers?: (
    searchTerm: string,
    page?: number
  ) => Promise<{ data: { users: UssdUser[]; total_count: number } }>;
  placeholder?: string;
}

/**
 * UssdCodeDropdown - Searchable, paginated dropdown that lists USSD codes.
 *
 * Selecting a USSD code returns the full user object so the caller can
 * auto-populate the read-only "User Name and Code" field.
 */
const UssdCodeDropdown: React.FC<UssdCodeDropdownProps> = ({
  onSelect,
  id,
  className,
  dropdownTopClass,
  testId,
  selectedValue,
  errorMessage,
  fetchUsers,
  placeholder = "Select",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState<UssdUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [internalSearchTerm, setInternalSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const loadUsers = useCallback(
    async (searchTerm: string, page: number = 1, append: boolean = false) => {
      if (!fetchUsers) return;
      setLoading(true);
      try {
        const result = await fetchUsers(searchTerm, page);
        const newUsers = result.data.users;
        const totalCount = result.data.total_count;
        setUsers((prev) => (append ? [...prev, ...newUsers] : newUsers));
        setCurrentPage(page);
        setHasMoreData(
          totalCount > (append ? users.length + newUsers.length : newUsers.length)
        );
      } catch (error) {
        console.error("Error fetching USSD codes:", error);
        if (!append) setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [fetchUsers]
  );

  // Load initial users when dropdown opens
  useEffect(() => {
    if (isOpen) {
      loadUsers("", 1);
    }
  }, [isOpen]);

  const handleSearch = (value: string) => {
    setInternalSearchTerm(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadUsers(value.trim(), 1);
    }, 500);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    const scrollPosition = Math.ceil(target.scrollTop + target.clientHeight);
    const threshold = target.scrollHeight - 20;
    if (scrollPosition >= threshold && hasMoreData && !loading) {
      loadUsers(internalSearchTerm.trim(), currentPage + 1, true);
    }
  };

  const handleClose = (action: boolean) => {
    setIsOpen(action);
    if (!action) {
      setInternalSearchTerm("");
      setUsers([]);
    }
  };

  const handleSelect = (user: UssdUser) => {
    onSelect && onSelect(user);
    setIsOpen(false);
    setInternalSearchTerm("");
    setUsers([]);
  };

  return (
    <DropDownModel isOpen={isOpen} handleClose={handleClose} modalStyle={className}>
      <div className="relative" data-testid={testId}>
        {/* Trigger button */}
        <div
          className={`flex items-center justify-between py-3 w-full ${
            selectedValue ? "text-black" : "text-gray-400"
          } h-14 text-subTitle ${isOpen ? "border-2 px-[15px]" : "border px-4"} ${
            errorMessage ? "border-red-500" : "border-P_light_active"
          } rounded-lg cursor-pointer`}
          onClick={() => handleClose(!isOpen)}
        >
          <span className="truncate max-w-[460px]">{selectedValue || placeholder}</span>
          <DropdownFramer isDropdownVisible={isOpen}>
            <Image src={"dropdown-down-arrow"} />
          </DropdownFramer>
        </div>

        {/* Search panel */}
        {isOpen && (
          <motion.div
            className={`${dropdownTopClass || "top-[57px]"} absolute left-0 w-full bg-white z-50 rounded-lg border border-P_light_active p-3.5`}
          >
            {/* Search input */}
            <div className="relative mb-3">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Image src={"active_search"} className="w-4 h-4" />
              </div>
              <input
                type="text"
                className="w-full px-4 py-2 pl-10 text-sm border border-P_light_active rounded-md focus:outline-none focus:border-p_primary"
                placeholder="Search"
                value={internalSearchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus
              />
            </div>

            {/* USSD code list */}
            <div
              className={`overflow-y-auto ${getFirefoxCssFix("scroll")} scroll-auto max-h-60`}
              onScroll={handleScroll}
            >
              {loading && users.length === 0 ? (
                <div className="space-y-3 animate-pulse">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <div className="h-5 bg-gray-200 rounded w-24" />
                    </div>
                  ))}
                </div>
              ) : users.length === 0 ? (
                <p className="py-2 text-sm text-gray-500">No data found.</p>
              ) : (
                users.map((user, index) => (
                  <div
                    key={index}
                    className="flex items-center p-2 mb-1 mr-1 text-sm cursor-pointer hover:bg-information_light hover:rounded-lg"
                    onClick={() => handleSelect(user)}
                    data-testid={`ussd-${user.ussd_code}`}
                  >
                    <span className="font-medium break-all">{user.ussd_code}</span>
                  </div>
                ))
              )}
              {loading && users.length > 0 && (
                <div className="flex justify-center py-2">
                  <div className="w-5 h-5 border-2 border-gray-300 rounded-full animate-spin border-t-p_primary" />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {errorMessage && (
        <div className="mb-1 mt-1">
          <ErrorMessage
            errorMessage={errorMessage}
            testId={`error-${testId || id || "ussd-code-dropdown"}`}
          />
        </div>
      )}
    </DropDownModel>
  );
};

export default UssdCodeDropdown;
